import { ProjectRenderer } from '../ProjectRenderer';
import { UUID } from '@/types/types';
import { RenderUtils } from '../utils/RenderUtils';
import {
	Vector2Tuple,
	Vector2,
	Group,
	Mesh,
	BoxGeometry,
	MeshStandardMaterial,
	DoubleSide,
	BackSide,
	Box3,
	BufferGeometry,
	Float32BufferAttribute,
	LineBasicMaterial,
	LineSegments,
	Vector3,
	BufferAttribute,
} from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { ROOM_WALL, ROOM_VERTEX: VERTEX } = RENDERER_CONFIG;

export interface SerializedPlaygroundVertex {
	id: UUID;
	position: Vector2Tuple;
	prev: null | UUID;
	next: null | UUID;
}

export enum VertexType {
	INNER,
	OUTER,
}

export class RoomVertex {
	static readonly POS_Y = ROOM_WALL.POS_Y;
	static readonly THICKNESS = ROOM_WALL.THICKNESS;
	static readonly COLORS = ROOM_WALL.COLORS;
	static readonly LAYER = VERTEX.LAYER;

	private type = VertexType.INNER;
	private materials = {
		base: {
			top: new MeshStandardMaterial({
				color: RoomVertex.COLORS.TOP,
				side: DoubleSide,
			}),
			hidden: new MeshStandardMaterial({
				transparent: true,
				opacity: 0,
			}),
			bottom: new MeshStandardMaterial({
				color: 0xffffff,
				side: BackSide,
			}),
			inside: new MeshStandardMaterial({
				color: RoomVertex.COLORS.SIDE,
				polygonOffset: true,
				polygonOffsetFactor: 1,
				polygonOffsetUnits: 1,
			}),
			red: new MeshStandardMaterial({
				color: 0xff0000,
				side: DoubleSide,
			}),
			outside: new MeshStandardMaterial({
				transparent: true,
				opacity: 0.7,
				color: RoomVertex.COLORS.SIDE,
			}),
		},
		hover: {
			top: new MeshStandardMaterial({
				color: RoomVertex.COLORS.HOVER,
				side: DoubleSide,
			}),
		},
		dragging: {
			top: new MeshStandardMaterial({
				color: RoomVertex.COLORS.DRAGGING,
				side: DoubleSide,
			}),
		},
	};

	private _position = new Vector2();
	private edgeLines: {
		horizontal: LineSegments | null;
		vertical: LineSegments | null;
	} = {
		horizontal: null,
		vertical: null,
	};

	id = crypto.randomUUID();
	mesh!: Mesh;
	group = new Group();
	prev: null | RoomVertex = null;
	next: null | RoomVertex = null;

	constructor(private renderer: ProjectRenderer, position: Vector2Tuple) {
		this.renderer = renderer;
		this._position = new Vector2(position[0], position[1]);
		this.group.position.set(
			this._position.x,
			this.renderer.roomManager.roomHeight / 2 + RoomVertex.POS_Y,
			this._position.y
		);
	}

	get position() {
		return this._position;
	}

	set position(pos: Vector2) {
		this._position = pos;

		this.group.position.set(
			pos.x,
			this.renderer.roomManager.roomHeight / 2 + RoomVertex.POS_Y,
			pos.y
		);
	}

	private calculateAngle() {
		const v1 = new Vector2()
			.subVectors(this.position, this.prev!.position)
			.normalize();
		const v2 = new Vector2()
			.subVectors(this.next!.position, this.position)
			.normalize();

		const cross = v1.x * v2.y - v1.y * v2.x;
		this.type = cross >= 0 ? VertexType.INNER : VertexType.OUTER;

		this.mesh.material = this.getMaterialsBasedOnAngle();
	}

	private getGeometry() {
		return new BoxGeometry(
			RoomVertex.THICKNESS,
			this.renderer.roomManager.roomHeight,
			RoomVertex.THICKNESS
		);
	}

	compute(updateGeometry = false) {
		if (updateGeometry) {
			this.mesh.geometry.dispose();
			this.mesh.geometry = this.getGeometry();
			this.group.position.setY(
				this.renderer.roomManager.roomHeight / 2 + RoomVertex.POS_Y
			);
		}
		this.disposeEdges();
		this.calculateAngle();
		this.createEdges();
	}

	init() {
		this.mesh = new Mesh(this.getGeometry(), this.getMaterialsBasedOnAngle());
		this.mesh.layers.set(RoomVertex.LAYER);
		this.group.add(this.mesh);
		this.renderer.room.add(this.group);
	}

	private getMaterialsBasedOnAngle() {
		// 0 – lewa
		// 1 – prawa
		// 2 – góra
		// 3 – dół
		// 4 – przód
		// 5 – tył

		const nodeSideMaterial =
			this.type === VertexType.INNER
				? this.materials.base.inside
				: this.materials.base.outside;

		const materials = [
			nodeSideMaterial,
			nodeSideMaterial,
			this.materials.base.top,
			this.materials.base.bottom,
			nodeSideMaterial,
			nodeSideMaterial,
		];

		return materials;
	}

	findInnerOuterPoints() {
		const pos = this.position;
		const prevPos = this.prev!.position;
		const nextPos = this.next!.position;

		const v1 = new Vector2().subVectors(pos, prevPos).normalize();
		const v2 = new Vector2().subVectors(nextPos, pos).normalize();

		const inward = new Vector2(-(v1.y + v2.y), v1.x + v2.x).normalize();

		const basePoints2D = this.getBasePoints().map((p) => new Vector2(p.x, p.z));

		let innerPoint: Vector2 | null = null;
		let outerPoint: Vector2 | null = null;
		let maxDistance = -Infinity;
		let minDistance = Infinity;

		for (const p of basePoints2D) {
			const toPoint = new Vector2().subVectors(p, pos);
			const distance = inward.dot(toPoint);

			if (distance > maxDistance) {
				maxDistance = distance;
				outerPoint = p;
			}
			if (distance < minDistance) {
				minDistance = distance;
				innerPoint = p;
			}
		}

		return { innerPoint, outerPoint };
	}

	private getBasePoints() {
		const bbox = new Box3().setFromBufferAttribute(
			this.mesh.geometry.attributes.position as BufferAttribute
		);

		const min = bbox.min;
		const max = bbox.max;

		return [
			new Vector3(min.x, min.y, min.z),
			new Vector3(max.x, min.y, min.z),
			new Vector3(max.x, min.y, max.z),
			new Vector3(min.x, min.y, max.z),
		];
	}

	private createVerticalEdges() {
		const { innerPoint, outerPoint } = this.findInnerOuterPoints();
		const bbox = new Box3().setFromBufferAttribute(
			this.mesh.geometry.attributes.position as BufferAttribute
		);
		const min = bbox.min;
		const max = bbox.max;

		const bottomPoints = [innerPoint, outerPoint].map(
			(p) => new Vector3(p!.x, min.y, p!.y)
		);
		const topPoints = bottomPoints.map((p) => new Vector3(p.x, max.y, p.z));

		const vertices: number[] = [];

		for (let i = 0; i < bottomPoints.length; i++) {
			vertices.push(...bottomPoints[i].toArray(), ...topPoints[i].toArray());
		}

		const geometry = new BufferGeometry();
		geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));

		const material = new LineBasicMaterial({
			color: RENDERER_CONFIG.COLORS.BLACK,
		});

		this.edgeLines.vertical = new LineSegments(geometry, material);
		this.edgeLines.vertical.layers.set(RoomVertex.LAYER);
		this.group.add(this.edgeLines.vertical);
	}

	private createHorizontalEdges() {
		const { innerPoint, outerPoint } = this.findInnerOuterPoints();
		if (!innerPoint || !outerPoint) return;

		const { outerPoint: outerPointNext, innerPoint: innerPointNext } =
			this.next!.findInnerOuterPoints();
		const { outerPoint: outerPointPrev, innerPoint: innerPointPrev } =
			this.prev!.findInnerOuterPoints();

		if (
			!outerPointNext ||
			!outerPointPrev ||
			!innerPointNext ||
			!innerPointPrev
		)
			return;

		const startPoint = this.type === VertexType.INNER ? innerPoint : outerPoint;
		const vertices: number[] = [];

		const dirToPrev = new Vector3()
			.subVectors(this.group.position, this.prev!.group.position)
			.normalize()
			.multiplyScalar(RoomVertex.THICKNESS)
			.negate();

		const dirToNext = new Vector3()
			.subVectors(this.group.position, this.next!.group.position)
			.normalize()
			.multiplyScalar(RoomVertex.THICKNESS)
			.negate();

		const heights = [
			-this.renderer.roomManager.roomHeight / 2,
			this.renderer.roomManager.roomHeight / 2,
		];

		for (const height of heights) {
			const start3D = new Vector3(startPoint.x, height, startPoint.y);

			const prevEnd = new Vector3(startPoint.x, height, startPoint.y).add(
				dirToPrev
			);
			vertices.push(...start3D.toArray(), ...prevEnd.toArray());

			const nextEnd = new Vector3(startPoint.x, height, startPoint.y).add(
				dirToNext
			);
			vertices.push(...start3D.toArray(), ...nextEnd.toArray());
		}

		const geometry = new BufferGeometry();
		geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));

		const material = new LineBasicMaterial({
			color: RENDERER_CONFIG.COLORS.BLACK,
		});

		this.edgeLines.horizontal = new LineSegments(geometry, material);
		this.edgeLines.horizontal.layers.set(RoomVertex.LAYER);
		this.group.add(this.edgeLines.horizontal);
	}

	createEdges() {
		this.disposeEdges();
		this.createVerticalEdges();
		this.createHorizontalEdges();
	}

	private disposeEdges() {
		if (this.edgeLines.horizontal)
			RenderUtils.disposeObject(this.edgeLines.horizontal);
		this.edgeLines.horizontal = null;

		if (this.edgeLines.vertical)
			RenderUtils.disposeObject(this.edgeLines.vertical);
		this.edgeLines.vertical = null;
	}

	dispose() {
		RenderUtils.disposeObject(this.group);
	}

	export(): SerializedPlaygroundVertex {
		return {
			id: this.id,
			position: [this.position.x, this.position.y],
			prev: this.prev ? this.prev.id : null,
			next: this.next ? this.next.id : null,
		};
	}
}
